import html2canvas from "html2canvas";
import jsPDF from "jspdf";

import { DEV_CONFIG } from "../../config/constants";

import { getDateString } from "../datetime/core";
import { handleImage, handleFiles } from "../../services/api/core";

/**
 * Capture the daily dispatch DOM to PDF/images for archive and email.
 *
 * @param {Function} setIsDispatchCapturing Loading setter for the capture overlay.
 * @param {Date} weekStartDate Week start used in filenames.
 * @param {Date} date Calendar day being captured.
 * @returns {Promise<void>}
 */
export const captureDispatch = async (
  setIsDispatchCapturing,
  weekStartDate,
  date
) => {
  // await document.fonts.ready; // This also allows time for the edit state switch to complete

  const weekStartString = getDateString(weekStartDate);
  const captureDate = new Date().toLocaleDateString();
  const captureTiime = new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  let element = null;
  const maxRetries = 10;
  let retries = 0;

  // It can take a small amount of time for the dispatch capture content to render. It
  // is not rendered by default for performance reasons. We need to wait for the render.
  while (!element && retries < maxRetries) {
    element = document.querySelector(".dispatch-content-to-capture");
    if (!element) {
      await new Promise((resolve) => setTimeout(resolve, 500));
      retries++;
    }
  }

  if (!element) {
    console.error("Element to capture not found");
    setIsDispatchCapturing(false);
    return;
  }

  setIsDispatchCapturing(false); // Reset the capturing state

  // Create a temporary container for the capture
  const tempContainer = document.createElement("div");
  tempContainer.style.width = "1428px"; // Fix the width
  tempContainer.style.position = "absolute"; // Remove from the document flow
  tempContainer.style.top = "-200vh"; // Move off-screen
  tempContainer.style.left = "-200vw"; // Move off-screen;
  tempContainer.style.zIndex = "-1";
  document.body.appendChild(tempContainer);

  // Add the header with the capture information
  const header = document.createElement("div");
  header.className = "text-center font-bold font-inter text-3xl mt-4 mb-12";
  const headerText = `
    <p>R&S Dispatch Capture for Week Starting ${weekStartString}</p>
    <p>Captured on ${captureDate} at ${captureTiime}</p>
  `;
  header.innerHTML = headerText;
  tempContainer.appendChild(header);

  // Clone the content into the temporary container
  const clonedElement = element.cloneNode(true);
  clonedElement.className = clonedElement.className.replace("hidden", ""); // Remove hidden class
  tempContainer.appendChild(clonedElement);

  // Replace WorkScopesSummary with EditWorkScopes in the cloned content
  const summaries = tempContainer.querySelectorAll(".work-scopes-summary");
  summaries.forEach((summary) => {
    const editWorkScopes = document.createElement("div");
    editWorkScopes.className = "edit-work-scopes";
    editWorkScopes.innerHTML = summary.innerHTML; // Assuming similar structure
    summary.parentNode.replaceChild(editWorkScopes, summary);
  });

  // Ensure the temporary container height is set to the full height of the content
  tempContainer.style.height = `${tempContainer.scrollHeight}px`;

  // Capture the canvas from the temporary container
  const canvas = await html2canvas(tempContainer, {
    scrollY: -window.scrollY,
    useCORS: true,
    scale: 1,
  });
  const imgData = canvas.toDataURL("image/jpeg", 0.7); // Use JPEG format with compression

  if (DEV_CONFIG.IS_DEVELOPMENT()) {
    // Download the image for debug
    const link = document.createElement("a");
    link.href = imgData;
    link.download = "daily_dispatch.png";
    link.click();
  }

  // Send the image to the backend for email attachment (backend only sends in prod)
  await handleImage({
    imgData: imgData,
    action: "email_dispatch_capture",
    week_start_date: weekStartString,
    dispatch_date: getDateString(date),
    capture_date: captureDate,
    capture_time: captureTiime,
  });

  // Remove the temporary container
  document.body.removeChild(tempContainer);

  // Generate the PDF
  const pdf = new jsPDF("p", "px", "letter");
  const imgProps = pdf.getImageProperties(imgData);
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
  const totalPages = Math.ceil(pdfHeight / pdf.internal.pageSize.getHeight());
  const filename = `${getDateString(date)} Daily Dispatch.pdf`;

  for (let i = 0; i < totalPages; i++) {
    if (i > 0) {
      pdf.addPage();
    }
    pdf.addImage(
      imgData,
      "JPEG", // JPEG to reduce final PDF size
      0,
      -i * pdf.internal.pageSize.getHeight(),
      pdfWidth,
      pdfHeight,
      undefined,
      "MEDIUM" // Use medium compression for a balance between quality and size
    );
  }

  if (DEV_CONFIG.IS_DEVELOPMENT()) {
    // Download the PDF for debug
    pdf.save("daily_dispatch.pdf");
  }

  // Send the PDF to the backend for egnyte upload (backend only sends in production)
  await handleFiles({
    files: [{ file: pdf, filename }],
    action: "upload_dispatch_pdf",
    additional_data: {
      week_start_date: weekStartString,
      captured_on_date: captureDate,
      captured_at_time: captureTiime,
      captured_date: getDateString(date),
    },
  });
};

/**
 * Capture job-report cards to PDF/images for the selected dispatch day.
 *
 * @param {Function} setIsJobReportCapturing Loading setter for the capture overlay.
 * @param {Date} weekStartDate Week start used in filenames.
 * @param {Date} date Calendar day being captured.
 * @param {Array} jobs Jobs included in the report.
 * @param {Array} scopes Scopes included in the report.
 * @returns {Promise<void>}
 */
export const captureJobReport = async (
  setIsJobReportCapturing,
  weekStartDate,
  date,
  jobs,
  scopes
) => {
  await document.fonts.ready; // This also allows time for the edit state switch to complete

  const weekStartString = getDateString(weekStartDate);
  const captureDate = new Date().toLocaleDateString();
  const captureTiime = new Date().toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  });

  /**
   * Deduplicates job/scope rows by `id` (first occurrence wins).
   *
   * @param {Array<Object>} array Rows with `id`.
   * @returns {Array<Object>} Unique rows.
   */
  function removeDuplicatesById(array) {
    const seenIds = new Set();
    return array.filter((item) => {
      if (seenIds.has(item.id)) {
        return false; // Skip if ID is already seen
      } else {
        seenIds.add(item.id);
        return true; // Keep if ID is new
      }
    });
  }

  // Calc how many jobs this day have scopes, and therefore how many capture elements
  // we are waiting on to be rendered.
  var jobsWithScopesCount = 0;
  if (scopes) {
    // Match JobReport: skipped aspects are omitted from Dispatch captures.
    var jobsWithScopes = scopes
      .filter((scope) => scope.skipped !== true)
      .map((scope) => {
        return jobs.find(
          (job) => job.id === scope.job.id && scope.date === getDateString(date)
        );
      })
      .filter((job) => job !== undefined); // Filter out undefined jobs
    jobsWithScopes = removeDuplicatesById(jobsWithScopes); // Remove duplicates
    jobsWithScopesCount = jobsWithScopes.length;
  }

  let elements = [];
  const maxRetries = 15;
  let retries = 0;

  // It can take a small amount of time for the job report capture content to render. It
  // is not rendered by default for performance reasons. We need to wait for the render.
  while (elements.length !== jobsWithScopesCount && retries < maxRetries) {
    elements = document.querySelectorAll(".jobreport-content-to-capture");
    if (elements.length !== jobsWithScopesCount) {
      await new Promise((resolve) => setTimeout(resolve, 500));
      retries++;
    }
  }

  if (elements.length !== jobsWithScopesCount) {
    console.error(
      `Some job elements to capture not found. Expected ${jobsWithScopesCount}, found ${elements.length}`
    );
    return;
  }

  setIsJobReportCapturing(false); // Reset the capturing state

  const imgDataArray = [];
  const pdfArray = [];

  // elements.length
  for (let i = 0; i < elements.length; i++) {
    const element = elements[i];
    const jobNumber = element.className.split(" ")[0];

    // Create a temporary container for the capture
    const tempContainer = document.createElement("div");
    tempContainer.style.width = "816px"; // Fix the width
    tempContainer.style.position = "absolute"; // Remove from the document flow
    tempContainer.style.top = "-100vh"; // Move off-screen
    tempContainer.style.left = "-100vw"; // Move off-screen;
    tempContainer.style.zIndex = "-1";
    tempContainer.style.backgroundColor = "white";
    document.body.appendChild(tempContainer);

    // Clone the content into the temporary container
    const clonedElement = element.cloneNode(true);
    clonedElement.className = clonedElement.className.replace("hidden", ""); // Remove hidden class
    tempContainer.appendChild(clonedElement);

    // Ensure the temporary container height is set to the full height of the content
    tempContainer.style.height = `${tempContainer.scrollHeight}px`;

    // Capture the canvas from the temporary container
    const canvas = await html2canvas(tempContainer, {
      scrollY: -window.scrollY,
      useCORS: true,
      scale: 1, // Increase the scale for better quality
    });
    const imgData = canvas.toDataURL("image/png");

    // // Download the image (useful for debug)
    // const link = document.createElement("a");
    // link.href = imgData;
    // link.download = `daily_report_${i + 1}.png`;
    // link.click();

    // Store the image data
    imgDataArray.push(imgData);

    // Remove the temporary container
    document.body.removeChild(tempContainer);

    // Generate the PDF
    const pdf = new jsPDF("p", "px", "letter");
    const imgProps = pdf.getImageProperties(imgData);
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
    const totalPages = Math.ceil(pdfHeight / pdf.internal.pageSize.getHeight());

    for (let j = 0; j < totalPages; j++) {
      if (j > 0) {
        pdf.addPage();
      }
      pdf.addImage(
        imgData,
        "JPEG", // JPEG to reduce final PDF size
        0,
        -j * pdf.internal.pageSize.getHeight(),
        pdfWidth,
        pdfHeight,
        undefined,
        "SLOW" // Use medium compression for a balance between quality and size
      );
    }

    if (DEV_CONFIG.IS_DEVELOPMENT()) {
      // Download the PDF (useful for debug)
      pdf.save(`test_daily_report_${i + 1}.pdf`);
    }

    // Store the PDF with the jobnumber as filename for backend processing
    pdfArray.push({ pdf, filename: jobNumber });
  }

  // Send the images to the backend for email attachment
  await handleImage({
    imgData: imgDataArray,
    action: "email_job_report",
    week_start_date: weekStartString,
    dispatch_date: getDateString(date),
    capture_date: captureDate,
    capture_time: captureTiime,
  });

  await handleFiles({
    files: pdfArray.map(({ pdf, filename }) => ({
      file: pdf,
      type: "pdf",
      filename,
    })),
    action: "upload_job_report_pdfs",
    additional_data: {
      week_start_date: weekStartString,
      captured_on_date: captureDate,
      captured_at_time: captureTiime,
      captured_date: getDateString(date),
    },
  });
};
